import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CheckOutlined, CloseOutlined, DollarOutlined, ReloadOutlined } from '@ant-design/icons';
import { Tag, Button, Modal, Input, Select, Switch, message, Descriptions } from 'antd';
import dayjs from 'dayjs';
import DataTable from '@/components/data-table';
import { PageHeader } from '@/components/page-header';
import { cn } from '@/libs/utils';
import { useTimeTrackingStore } from '@/store/useTimeTrackingStore';
import { STATUS_COLORS } from '../_shared/constants';
import {
  useTimeTrackingCompanies,
  useDropUnavailableCompany,
  usePendingEntries,
  useApproveEntries,
  useRejectEntries,
} from '../_shared/hooks';
import { minutesToHHMM } from '../_shared/utils';
import { getApprovalColumns } from './_components/ApprovalColumns';

const { TextArea } = Input;

const FILTER_KEYS = ['employeeName', 'projectName', 'description'];
const SORT_PREFIX = 'sort';

const TITLE_MAP = {
  SUBMITTED: 'pendingApproval',
  APPROVED: 'approved',
  all: 'allEntries',
};

function ApprovalPage() {
  const { t } = useTranslation(['forms', 'tables']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [detailEntry, setDetailEntry] = useState(null);
  const [approveBillable, setApproveBillable] = useState(null); // null = don't change, true/false = set

  const { data: companyData, isLoading: companyIsLoading } = useTimeTrackingCompanies();
  const { selectedCompany, setSelectedCompany } = useTimeTrackingStore();

  // Read all state from URL
  const company = searchParams.get('company') || '';
  const statusFilter = searchParams.get('status') || 'SUBMITTED';
  const offset = searchParams.get('offset') || '1';
  const limit = searchParams.get('limit') || '10';

  const updateParams = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const clearFiltersAndSorts = useCallback(
    (extra = {}) => {
      const next = new URLSearchParams();
      // Keep only company, status, offset, limit
      if (company) next.set('company', company);
      const st = extra.status || statusFilter;
      if (st && st !== 'SUBMITTED') next.set('status', st);
      next.set('offset', '1');
      if (limit !== '10') next.set('limit', limit);
      setSearchParams(next, { replace: true });
    },
    [company, statusFilter, limit, setSearchParams]
  );

  const hasActiveFilters = useMemo(() => {
    for (const [k] of searchParams.entries()) {
      if (k.startsWith(SORT_PREFIX) || FILTER_KEYS.includes(k)) return true;
    }
    return false;
  }, [searchParams]);

  const dropCompany = useCallback(() => {
    setSelectedCompany(null);
    updateParams({ company: undefined });
  }, [setSelectedCompany, updateParams]);

  useDropUnavailableCompany(companyData, company, dropCompany);

  // Auto-select company
  useEffect(() => {
    if (company) return;
    if (companyData?.isSingle) {
      updateParams({ company: companyData.options[0].value });
    } else if (selectedCompany) {
      updateParams({ company: selectedCompany });
    }
  }, [companyData, company, selectedCompany, updateParams]);

  // Sync Zustand
  useEffect(() => {
    if (company && company !== selectedCompany) {
      setSelectedCompany(company);
    }
  }, [company, selectedCompany, setSelectedCompany]);

  // Build filters for backend
  const filters = useMemo(() => {
    if (!company) return {};
    const f = { company, status: statusFilter, offset, limit };
    // Pass through sort and text filter params
    for (const [k, v] of searchParams.entries()) {
      if (k.startsWith('sort') || ['employeeName', 'projectName', 'description'].includes(k)) {
        f[k] = v;
      }
    }
    return f;
  }, [searchParams, company, statusFilter, offset, limit]);

  const hasCompany = !!company;

  const { data: result, status: queryStatus } = usePendingEntries(
    hasCompany ? filters : {},
    { enabled: hasCompany }
  );

  const entries = useMemo(() => result?.data || [], [result]);
  const total = result?.total || 0;
  const canApprove = result?.canApprove ?? false;
  const ownEmployeeId = result?.ownEmployeeId || null;

  const canApproveEntry = useCallback(
    (record) => {
      if (!canApprove) return false;
      if (!ownEmployeeId) return true;
      const entryEmpId =
        record.employee?._id?.toString?.() || record.employee?.toString?.();
      return entryEmpId !== ownEmployeeId.toString();
    },
    [canApprove, ownEmployeeId]
  );

  const approveMutation = useApproveEntries();
  const rejectMutation = useRejectEntries();

  const handleApprove = useCallback(
    async (ids, billableOverride) => {
      try {
        const payload = { entryIds: ids };
        const b = billableOverride ?? approveBillable;
        if (typeof b === 'boolean') payload.billable = b;
        await approveMutation.mutateAsync(payload);
        message.success(t('entriesApproved', { count: ids.length }));
        setSelectedRowKeys([]);
        setDetailEntry(null);
        setApproveBillable(null);
      } catch {
        message.error(t('failedToApprove'));
      }
    },
    [approveMutation, approveBillable, t]
  );

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectReason.trim() || selectedRowKeys.length === 0) return;
    try {
      await rejectMutation.mutateAsync({
        entryIds: selectedRowKeys,
        reason: rejectReason.trim(),
      });
      message.success(t('entriesRejected', { count: selectedRowKeys.length }));
      setSelectedRowKeys([]);
      setRejectOpen(false);
      setRejectReason('');
      setDetailEntry(null);
    } catch {
      message.error(t('failedToReject'));
    }
  }, [rejectMutation, selectedRowKeys, rejectReason, t]);

  const statusOptions = [
    { label: t('pendingApproval'), value: 'SUBMITTED' },
    { label: t('approved'), value: 'APPROVED' },
    { label: t('allEntries'), value: 'all' },
  ];

  const hasSelected = selectedRowKeys.length > 0;

  const columns = useMemo(
    () =>
      getApprovalColumns({
        searchParams,
        updateParams,
        t,
        canApprove,
        statusFilter,
        canApproveEntry,
        handleApprove,
        onReject: (id) => {
          setSelectedRowKeys([id]);
          setRejectOpen(true);
        },
      }),
    [searchParams, updateParams, t, canApprove, statusFilter, canApproveEntry, handleApprove]
  );

  const handlePaginationChange = useCallback(
    (key, value) => {
      updateParams({ [key]: value });
    },
    [updateParams]
  );

  // The company picker is the header's only content. With a single company
  // there is nothing to show, and the bar must go with it: `page__header` keeps
  // its `min-h-12` even when empty, which left a blank band above the table.
  const showCompanySelect = !companyData?.isSingle;

  return (
    <>
      {showCompanySelect && (
        <PageHeader>
          <div className="flex w-full items-center gap-2 overflow-x-auto bg-white p-1 scrollbar-hide">
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('choose', { value: t('company') })}
              className="h-[38px] w-full max-w-[394px] rounded outline outline-accent"
              loading={companyIsLoading}
              options={companyData?.options || []}
              value={company || undefined}
              onChange={(value) =>
                updateParams({ company: value, offset: '1', limit: '10' })
              }
            />
          </div>
        </PageHeader>
      )}

      {!hasCompany ? (
        <div className="py-12 text-center text-gray-400">
          {t('selectCompany')}
        </div>
      ) : (
        <DataTable
          total={total}
          data={entries}
          title={t(TITLE_MAP[statusFilter])}
          columns={columns}
          offset={parseInt(offset)}
          limit={parseInt(limit)}
          onChangePagination={handlePaginationChange}
          className="h-[calc(100svh_-_164px)]"
          status={queryStatus}
          rowSelection={
            canApprove && statusFilter === 'SUBMITTED'
              ? {
                  selectedRowKeys,
                  onChange: setSelectedRowKeys,
                  getCheckboxProps: (record) => ({
                    disabled: !canApproveEntry(record),
                  }),
                }
              : undefined
          }
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => setDetailEntry(record),
            className: 'cursor-pointer',
          })}
          headerChildren={
            <>
              <div className="flex items-center gap-0.5 rounded-full bg-gray-100 p-0.5">
                {statusOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      clearFiltersAndSorts({ status: opt.value });
                      setSelectedRowKeys([]);
                    }}
                    className={cn(
                      'whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium transition-all',
                      statusFilter === opt.value
                        ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                        : 'text-gray-500 hover:text-gray-700'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {hasActiveFilters && (
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={() => clearFiltersAndSorts()}
                  className="btn-primary-reverse h-8"
                >
                  {t('resetFilters')}
                </Button>
              )}
              {canApprove && hasSelected && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1">
                    <DollarOutlined className="text-xs text-gray-400" />
                    <span className="text-xs text-gray-500">{t('billable')}</span>
                    <Switch
                      size="small"
                      checked={approveBillable === true}
                      onChange={(checked) =>
                        setApproveBillable(
                          approveBillable === null
                            ? checked
                            : approveBillable === checked
                              ? null
                              : checked
                        )
                      }
                    />
                    {approveBillable !== null && (
                      <button
                        type="button"
                        onClick={() => setApproveBillable(null)}
                        className="ml-0.5 text-[10px] text-gray-400 hover:text-gray-600"
                        title={t('resetBillable')}
                      >
                        <CloseOutlined style={{ fontSize: 8 }} />
                      </button>
                    )}
                  </div>
                  <Button
                    type="default"
                    icon={<CheckOutlined />}
                    onClick={() => handleApprove(selectedRowKeys)}
                    loading={approveMutation.isPending}
                    className="btn-default !h-8 !rounded-full !border-white !bg-white !px-4 !text-green-600 hover:!bg-green-50"
                  >
                    {t('approveAll')} ({selectedRowKeys.length})
                  </Button>
                  <Button
                    type="default"
                    icon={<CloseOutlined />}
                    onClick={() => setRejectOpen(true)}
                    className="btn-default !h-8 !rounded-full !border-white !bg-white !px-4 !text-red-600 hover:!bg-red-50"
                  >
                    {t('rejectAll')} ({selectedRowKeys.length})
                  </Button>
                </div>
              )}
            </>
          }
        />
      )}

      <Modal
        title={t('entryDetails')}
        open={!!detailEntry}
        onCancel={() => setDetailEntry(null)}
        footer={null}
        width={520}
      >
        {detailEntry && (
          <Descriptions column={1} bordered size="small" className="mt-4">
            {canApprove && (
              <Descriptions.Item label={t('employee')}>
                {`${detailEntry.employee?.firstName || ''} ${detailEntry.employee?.lastName || ''}`.trim() || '-'}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('date')}>
              {dayjs(detailEntry.date).format('DD.MM.YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label={t('project')}>
              <span className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: detailEntry.project?.color || '#1890ff' }}
                />
                {detailEntry.project?.name || '-'}
              </span>
            </Descriptions.Item>
            {detailEntry.task && (
              <Descriptions.Item label={t('task')}>
                {detailEntry.project?.tasks?.find(
                  (tk) => tk._id?.toString() === detailEntry.task?.toString()
                )?.name || '-'}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('duration')}>
              <span className="font-mono">{minutesToHHMM(detailEntry.duration)}</span>
            </Descriptions.Item>
            <Descriptions.Item label={t('billable')}>
              {detailEntry.billable ? <Tag color="green">$</Tag> : <Tag>-</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={t('status', { ns: 'tables' })}>
              <Tag color={STATUS_COLORS[detailEntry.status]}>{t(detailEntry.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('description')}>
              {detailEntry.description || '-'}
            </Descriptions.Item>
            {detailEntry.rejectionReason && (
              <Descriptions.Item label={t('rejectionReason')}>
                {detailEntry.rejectionReason}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={t('rejectionReason')}
        open={rejectOpen}
        onCancel={() => {
          setRejectOpen(false);
          setRejectReason('');
        }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              className="btn-default"
              onClick={() => {
                setRejectOpen(false);
                setRejectReason('');
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              className="btn-danger inline-flex !text-white"
              disabled={!rejectReason.trim()}
              loading={rejectMutation.isPending}
              onClick={handleRejectConfirm}
            >
              {t('reject')}
            </Button>
          </div>
        }
      >
        <TextArea
          rows={3}
          placeholder={t('rejectionPlaceholder')}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          maxLength={500}
        />
      </Modal>
    </>
  );
}

export default ApprovalPage;
